import { Formik } from "formik";
import React from "react";
import { View } from "react-native";
import { updateEventAlias } from "../http/events";
import { AliasSchema } from "../screens/Schemas";
import { eventLivePageBase } from "../globalUtils";
import { useActionSheet } from "@/components/hooks/useActionSheet";
import { notify } from "./AppToast";
import Button, { ButtonRow, CopyButton } from "@/components/settings/Button";
import Callout from "@/components/settings/Callout";
import { Field, TextInputBox } from "@/components/settings/Field";
import { UnsavedChanges } from "@/components/settings/SaveStatus";
import tw from "twrnc";
import { t } from "@/localization";

// The event link (evt.live/<alias>). Changing it breaks links already shared,
// so it has its own button and a confirmation instead of auto-save.
const EventAliasForm = ({ event }) => {
  const { slug, alias, is_test } = event;
  const { showActionSheetWithOptions } = useActionSheet();

  const confirmLinkChange = (submitFunction) => {
    showActionSheetWithOptions(
      {
        options: [t("Change Link"), t("Cancel")],
        cancelButtonIndex: 1,
        destructiveButtonIndex: 0,
        title: t("Change event link?"),
        message: t(
          "If you've already shared the current link with guests, they won't be able to access the event with the old link anymore. Are you sure you want to proceed?"
        ),
      },
      async (buttonIndex) => {
        if (buttonIndex === 0) {
          submitFunction();
        }
      }
    );
  };

  if (is_test) {
    return null; // Don't show for test events
  }

  return (
    <Formik
      initialValues={{
        alias: alias || "",
      }}
      onSubmit={async (values, actions) => {
        actions.setSubmitting(true);
        try {
          await updateEventAlias({ slug, ...values });
          actions.resetForm({ values });
          notify.success(
            t("Event link changed"),
            `${eventLivePageBase}${values.alias}`
          );
        } catch (err) {
          actions.setSubmitting(false);
          notify.error(t("Couldn't change the link"), err?.message);
        }
      }}
      validationSchema={AliasSchema}
      validateOnBlur={false}
      validateOnChange={true}
    >
      {(f) => {
        const changed = f.values.alias !== (f.initialValues.alias || "");
        const canSubmit =
          changed && !!f.values.alias && !f.errors.alias && !f.isSubmitting;
        return (
          <View>
            <UnsavedChanges dirty={changed} />
            <Field
              error={
                (f.touched.alias || changed) && f.values.alias
                  ? f.errors.alias
                  : null
              }
              hint={changed ? null : t("Share this link with your guests.")}
              last
            >
              <TextInputBox
                prefix="evt.live/"
                value={f.values.alias}
                onChangeText={f.handleChange("alias")}
                onBlur={f.handleBlur("alias")}
                placeholder={t("your-event-name")}
                accessibilityLabel={t("Event link")}
                autoCapitalize="none"
                autoCorrect={false}
                error={
                  (f.touched.alias || changed) && f.values.alias
                    ? f.errors.alias
                    : null
                }
                onSubmitEditing={() =>
                  canSubmit && confirmLinkChange(() => f.handleSubmit())
                }
                actions={
                  !changed && f.values.alias
                    ? [
                        {
                          icon: "copy-outline",
                          label: t("Copy event link"),
                          copy: `${eventLivePageBase}${f.values.alias}`,
                          toast: t("Link copied"),
                          toastDetail: `${eventLivePageBase}${f.values.alias}`,
                        },
                      ]
                    : undefined
                }
              />
            </Field>

            {changed ? (
              <>
                <Callout tone="warning" style={tw`mt-3`}>
                  {t(
                    "Guests who already have the old link won't be able to open the event with it."
                  )}
                </Callout>
                <ButtonRow style={tw`mt-3`}>
                  <Button
                    text={t("Cancel")}
                    variant="tertiary"
                    size="md"
                    fullWidth={false}
                    disabled={f.isSubmitting}
                    onPress={() => f.resetForm()}
                  />
                  <Button
                    text={t("Change link")}
                    variant="primary"
                    size="md"
                    fullWidth={false}
                    icon="link-outline"
                    loading={f.isSubmitting}
                    disabled={!canSubmit}
                    onPress={() => confirmLinkChange(() => f.handleSubmit())}
                  />
                </ButtonRow>
              </>
            ) : (
              !!f.values.alias && (
                <ButtonRow align="start" style={tw`mt-3`}>
                  <CopyButton
                    value={`${eventLivePageBase}${f.values.alias}`}
                    text={t("Copy link")}
                    toast={t("Link copied")}
                    toastDetail={`${eventLivePageBase}${f.values.alias}`}
                    size="md"
                    fullWidth={false}
                  />
                </ButtonRow>
              )
            )}
          </View>
        );
      }}
    </Formik>
  );
};

export default EventAliasForm;
